import { getCurrency } from '@common/utils/currency.ts'
import type { ColumnDef } from '@tanstack/react-table'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { EditLinkClicks } from '@/modules/statistics/onlyfans/ui/DetailedStatistics/EditLinkClicks.tsx'

import type { DetailedStatisticsType } from '../../types'
import { tableColumnTitles } from './consts'

export const getDetailedStatisticsColumns = (
  showCents = false,
): ColumnDef<DetailedStatisticsType | undefined>[] => [
  {
    accessorKey: 'date',
    header: tableColumnTitles.date,
    cell: ({ row }) => {
      return (
        <div className="w-fit whitespace-nowrap">{row.getValue('date')}</div>
      )
    },
  },
  {
    accessorKey: 'totalRevenue',
    header: tableColumnTitles.totalRevenue,
    cell: ({ row }) => {
      return (
        <div>
          {getCurrency(row.getValue('totalRevenue'), showCents ? 2 : 0)}
        </div>
      )
    },
  },
  {
    accessorKey: 'newSubs',
    header: tableColumnTitles.newSubs,
  },
  {
    accessorKey: 'newSubscriptionsRevenue',
    header: tableColumnTitles.newSubscriptionsRevenue,
    cell: ({ row }) => {
      return (
        <div>
          {getCurrency(
            row.getValue('newSubscriptionsRevenue'),
            showCents ? 2 : 0,
          )}
        </div>
      )
    },
  },
  {
    accessorKey: 'recurringSubscriptionsRevenue',
    header: tableColumnTitles.recurringSubscriptionsRevenue,
    cell: ({ row }) => {
      return (
        <div>
          {getCurrency(
            row.getValue('recurringSubscriptionsRevenue'),
            showCents ? 2 : 0,
          )}
        </div>
      )
    },
  },
  {
    accessorKey: 'tipsRevenue',
    header: tableColumnTitles.tipsRevenue,
    cell: ({ row }) => {
      return (
        <div>{getCurrency(row.getValue('tipsRevenue'), showCents ? 2 : 0)}</div>
      )
    },
  },
  {
    accessorKey: 'messageRevenue',
    header: tableColumnTitles.messageRevenue,
    cell: ({ row }) => {
      return (
        <div>
          {getCurrency(row.getValue('messageRevenue'), showCents ? 2 : 0)}
        </div>
      )
    },
  },
  {
    accessorKey: 'textingRatio',
    header: () => {
      return (
        <Tooltip
          content="The money earned from tips and messages, divided by the revenue from subscribers"
          className="flex items-center justify-center gap-1"
        >
          {tableColumnTitles.textingRatio}
          <InfoIcon className="h-4 w-4 flex-shrink-0" />
        </Tooltip>
      )
    },
  },
  {
    accessorKey: 'openChats',
    header: tableColumnTitles.openChats,
  },
  {
    accessorKey: 'sellingChats',
    header: tableColumnTitles.sellingChats,
  },
  {
    accessorKey: 'averageFanSpend',
    header: () => {
      return (
        <Tooltip
          content="Measures the average spend per fan of any fan that spend money during the selected period."
          className="flex items-center justify-center gap-1"
        >
          {tableColumnTitles.averageFanSpend}
          <InfoIcon className="h-4 w-4 flex-shrink-0" />
        </Tooltip>
      )
    },
    cell: ({ row }) => (
      <Tooltip
        content={
          row.getValue('averageFanSpend')
            ? `Total revenue $${row.original?.totalRevenue} by ${row.original?.spendingFansCount} fans`
            : ''
        }
        className="flex items-center justify-center gap-1"
      >
        {getCurrency(row.getValue('averageFanSpend') ?? 0, 2)}
      </Tooltip>
    ),
  },
  {
    accessorKey: 'linkClicks',
    header: tableColumnTitles.linkClicks,
    cell: ({ row }) => <EditLinkClicks row={row} />,
  },
  {
    accessorKey: 'conversionRate',
    header: tableColumnTitles.conversionRate,
  },
]
